import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { getDictionaryConfig } from "@/containers/dictionaries/getDictionaryConfig";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { ApiError } from "@/services/api";
import { ReadonlyObject } from "@/types/readOnly";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Module } from "@/constants";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { DepartmentBasedProjectRatesForm } from "@/modules/departmentBasedProjectRates/components/DepartmentBasedProjectRatesForm";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

export default function DepartmentDictionaryCreator<
  TRead extends ReadonlyObject,
  TWrite extends object,
>({ id, dict }: DictionaryContext) {
  const { t } = useTranslation();
  const config = getDictionaryConfig(dict);
  const navigate = useNavigate();
  const { toast } = useToast();
  const labels = config.getLabels(t);
  const { invalidateListQuery } = useQueryCacheUpdater(
    Module.DepartmentBasedProjectRates,
  );
  const { can_create } = useModulePermissions();

  const mutationFn: MutationFunction<TRead, TWrite> = async (values) => {
    const response = (await config.service.post(values)) as TRead;

    return {
      ...values,
      id: response?.id || id,
    } as unknown as TRead;
  };

  const mutation = useMutation<TRead, ApiError, TWrite>({
    mutationFn,
    onSuccess: (response) => {
      const id = response.id;
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.create,
      });
      invalidateListQuery();
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<TWrite>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues || {}) as DefaultValues<TWrite>,
    disabled: mutation.isPending || !can_create,
  });

  useFormUnsavedChangesWarning(form);

  return (
    <DictionaryRecordContextProvider
      value={{
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      <DepartmentBasedProjectRatesForm />
    </DictionaryRecordContextProvider>
  );
}
